/**
 * Floating AI Agent Button
 * Persistent button that opens AI chat
 */
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Tooltip } from 'antd';
import { MessageCircleIcon, Sparkles } from 'lucide-react';
import { useAIAgent } from '../../context/AIAgentContext';
import { useAuth } from '../../context/AuthContext';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
  }
`;

const ButtonContainer = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.3s ease;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
`;

const Button = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-primary);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: var(--color-primary-dark);
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &.has-updates {
    animation: ${pulse} 2s infinite;
  }
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SparkleIcon = styled(Sparkles)`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  color: var(--color-warning);
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background: var(--color-danger);
  border-radius: 50%;
  border: 2px solid white;
`;

export const FloatingAgentButton: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { isVisible, toggleAgent, actionHistory } = useAIAgent();
  
  // Don't show if user is not logged in
  if (!user) {
    return null;
  }
  
  // Don't show on login/auth pages
  const isAuthPage = window.location.pathname.includes('/login') || 
                    window.location.pathname.includes('/auth');
  if (isAuthPage) {
    return null;
  }
  
  // Check if there are recent actions (for notification)
  const hasRecentActions = actionHistory.some(
    action => new Date().getTime() - action.timestamp.getTime() < 300000 // 5 minutes
  );

  return (
    <ButtonContainer $visible={!isVisible}>
      <Tooltip 
        title="AI Assistant (Ctrl+K)" 
        placement="left"
      >
        <Button 
          onClick={toggleAgent}
          className={hasRecentActions ? 'has-updates' : ''}
          aria-label="Open AI Assistant"
        >
          <IconWrapper>
            <MessageCircleIcon size={24} />
            <SparkleIcon />
          </IconWrapper>
          {hasRecentActions && <NotificationDot />}
        </Button>
      </Tooltip>
    </ButtonContainer>
  );
};